import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FaArrowCircleUp, FaTrash, FaEdit, FaEye, FaTimes, FaToggleOn, FaToggleOff, FaPlus } from "react-icons/fa";
import { FiRefreshCw, FiFilter } from "react-icons/fi";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

const SimpleBannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    type: 'header',
    title: '',
    description: '',
    platform: 'all',
    redirectUrl: '',
    order: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [filteredBanners, setFilteredBanners] = useState([]);

  const bannerTypes = [
    { value: 'header', label: 'Header Banner' },
    { value: 'special_for_you', label: 'Special For You' }
  ];

  const platforms = [
    { value: 'all', label: 'All Platforms' },
    { value: 'web', label: 'Web' },
    { value: 'mobile', label: 'Mobile' }
  ];

  // Fetch all banners
  const fetchBanners = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authentication token is missing.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/banners`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        setBanners(data.data);
        setFilteredBanners(data.data);
      } else {
        setUploadError("Failed to fetch banners.");
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      setUploadError("Network error while fetching banners.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...banners];

    if (typeFilter) {
      filtered = filtered.filter(banner => banner.type === typeFilter);
    }

    if (statusFilter) {
      if (statusFilter === 'active') {
        filtered = filtered.filter(banner => banner.status === true);
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(banner => banner.status === false);
      }
    }

    if (platformFilter) {
      filtered = filtered.filter(banner => banner.platform === platformFilter);
    }

    setFilteredBanners(filtered);
  }, [banners, typeFilter, statusFilter, platformFilter]);

  // Reset filters
  const resetFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setPlatformFilter('');
  };

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      // if (file.size > 5 * 1024 * 1024) {
      //   setUploadError("File size must be less than 5MB");
      //   return;
      // }
      
      if (!file.type.startsWith('image/')) {
        setUploadError("Please select a valid image file");
        return;
      }
      
      setSelectedFile(file);
      setUploadError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']
    },
    multiple: false,
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      type: 'header',
      title: '',
      description: '',
      platform: 'all',
      redirectUrl: '',
      order: 0
    });
    setSelectedFile(null);
    setSelectedBanner(null);
    setUploadError(null);
    setUploadSuccess(null);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedFile && !selectedBanner) {
      setUploadError("Please select an image file.");
      return;
    }

    setUploadLoading({ [selectedBanner?._id || 'new']: true });
    setUploadError(null);
    setUploadSuccess(null);

    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authentication token is missing.");
      setUploadLoading({});
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("type", formData.type);
    formDataToSend.append("title", formData.title.trim());
    formDataToSend.append("description", formData.description.trim());
    formDataToSend.append("platform", formData.platform);
    formDataToSend.append("redirectUrl", formData.redirectUrl.trim());
    
    if (formData.type === 'special_for_you') {
      formDataToSend.append("order", formData.order || 0);
    }
    
    if (selectedFile) {
      formDataToSend.append("image", selectedFile);
    }

    try {
      const url = selectedBanner
        ? `${API_BASE_URL}/api/admin/banner/${selectedBanner._id}`
        : `${API_BASE_URL}/api/admin/banner`;
      
      const method = selectedBanner ? "PATCH" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { Authorization: `Bearer ${token}` },
        body: formDataToSend,
      });

      const data = await response.json();
      if (data.success) {
        setUploadSuccess(
          selectedBanner 
            ? "Banner updated successfully!" 
            : "Banner created successfully!"
        );
        resetForm();
        setShowUploadModal(false);
        setShowEditModal(false);
        fetchBanners();
        
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error processing banner.");
      }
    } catch (error) {
      console.error("Error processing banner:", error);
      setUploadError("Network error occurred. Please try again.");
    } finally {
      setUploadLoading({});
    }
  };

  // Handle delete
  const handleDelete = async (bannerId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authentication token is missing.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/banner/${bannerId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        setUploadSuccess("Banner deleted successfully!");
        fetchBanners();
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error deleting banner.");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      setUploadError("Network error occurred while deleting.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (banner) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authentication token is missing.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/banner/${banner._id}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: !banner.status }),
      });

      const data = await response.json();
      if (data.success) {
        setUploadSuccess(`Banner ${!banner.status ? 'activated' : 'deactivated'} successfully!`);
        fetchBanners();
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error updating banner status.");
      }
    } catch (error) {
      console.error("Error updating banner status:", error);
      setUploadError("Network error occurred while updating status.");
    }
  };

  // Handle edit
  const handleEdit = (banner) => {
    setSelectedBanner(banner);
    setFormData({
      type: banner.type,
      title: banner.title || '',
      description: banner.description || '',
      platform: banner.platform,
      redirectUrl: banner.redirectUrl || '',
      order: banner.order || 0
    });
    setSelectedFile(null);
    setShowEditModal(true);
  };

  // Handle view
  const handleView = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get current header banners summary
  const getHeaderSummary = () => {
    const allHeaderCount = banners.filter(b => b.type === 'header' && b.platform === 'all' && b.status).length;
    const webHeaderCount = banners.filter(b => b.type === 'header' && b.platform === 'web' && b.status).length;
    const mobileHeaderCount = banners.filter(b => b.type === 'header' && b.platform === 'mobile' && b.status).length;
    const specialCount = banners.filter(b => b.type === 'special_for_you' && b.status).length;

    return { allHeaderCount, webHeaderCount, mobileHeaderCount, specialCount };
  };

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const { allHeaderCount, webHeaderCount, mobileHeaderCount, specialCount } = getHeaderSummary();

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-card p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Banner Management</h2>
            <p className="text-gray-600 mt-1">Manage header banners and special promotion banners</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaPlus className="mr-2" />
              Add Banner
            </button>
            <button
              onClick={fetchBanners}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {uploadSuccess && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {uploadSuccess}
          </div>
        )}
        {uploadError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {uploadError}
          </div>
        )}

        {/* Quick Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h4 className="font-semibold text-indigo-800">All Platforms Header</h4>
            <p className="text-sm text-indigo-600">
              {allHeaderCount > 0 ? `${allHeaderCount} active banner${allHeaderCount > 1 ? 's' : ''}` : 'No active headers'}
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800">Web Header</h4>
            <p className="text-sm text-blue-600">
              {webHeaderCount > 0 ? `${webHeaderCount} active banner${webHeaderCount > 1 ? 's' : ''}` : 'No active headers'}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800">Mobile Header</h4>
            <p className="text-sm text-green-600">
              {mobileHeaderCount > 0 ? `${mobileHeaderCount} active banner${mobileHeaderCount > 1 ? 's' : ''}` : 'No active headers'}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-800">Special Banners</h4>
            <p className="text-sm text-purple-600">{specialCount} active banners</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <FiFilter className="mr-1" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            
            <button
              onClick={resetFilters}
              className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              <FiRefreshCw className="mr-1" /> Reset Filters
            </button>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg mt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banner Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {bannerTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Platforms</option>
                  {platforms.map(platform => (
                    <option key={platform.value} value={platform.value}>{platform.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Banners List */}
      <div className="bg-white rounded-3xl shadow-card p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-6">
          All Banners ({filteredBanners.length})
        </h3>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading banners...</span>
          </div>
        ) : filteredBanners.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No banners found.</p>
            <p className="text-sm">Add your first banner using the button above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBanners.map((banner) => (
              <div
                key={banner._id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h5 className="font-semibold text-gray-800 truncate">
                      {banner.title || 'Untitled Banner'}
                    </h5>
                    <p className="text-sm text-gray-600">
                      {bannerTypes.find(t => t.value === banner.type)?.label}
                    </p>
                    </div>
                  <button
                    onClick={() => handleToggleStatus(banner)}
                    className={`p-1 rounded ${
                      banner.status ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-200'
                    }`}
                    title={banner.status ? 'Active' : 'Inactive'}
                  >
                    {banner.status ? <FaToggleOn className="text-lg" /> : <FaToggleOff className="text-lg" />}
                  </button>
                </div>
                
                {banner.image && (
                  <img
                    src={banner.image}
                    alt={banner.title || 'Banner'}
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                )}
                
                <div className="flex justify-between items-center mb-3">
                  <div className="flex space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      banner.status ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {banner.status ? 'Active' : 'Inactive'}
                    </span>
                    
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {banner.platform}
                    </span>

                    {banner.type === 'special_for_you' && (
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                        Order: {banner.order || 0}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    {formatDate(banner.createdAt)}
                  </span>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleView(banner)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                      title="View"
                    >
                      <FaEye className="text-sm" />
                    </button>
                    <button
                      onClick={() => handleEdit(banner)}
                      className="p-2 text-yellow-600 hover:bg-yellow-100 rounded transition-colors"
                      title="Edit"
                    >
                      <FaEdit className="text-sm" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(banner)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                      title="Delete"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold">Add New Banner</h3>
              <button
                onClick={() => { setShowUploadModal(false); resetForm(); }}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Banner Type & Platform Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Banner Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {bannerTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.type === 'header' ? 'Multiple header banners displayed as carousel' : 'Multiple banners allowed with custom order'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Platform *</label>
                  <select
                    name="platform"
                    value={formData.platform}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {platforms.map(platform => (
                      <option key={platform.value} value={platform.value}>{platform.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image *</label>
                <div className="border border-gray-300 border-dashed rounded-xl hover:border-blue-500 transition-colors">
                  <div
                    {...getRootProps()}
                    className={`p-6 cursor-pointer transition-all ${
                      isDragActive ? "border-blue-500 bg-blue-50" : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <input {...getInputProps()} />
                    <div className="flex flex-col items-center">
                      <div className="mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200">
                          <FaArrowCircleUp className="text-xl" />
                        </div>
                      </div>
                      <h4 className="mb-2 font-medium text-gray-800">
                        {isDragActive ? "Drop Files Here" : "Drag & Drop Banner Image"}
                      </h4>
                      <span className="text-sm text-gray-600 mb-2">
                        PNG, JPG, WebP, SVG images (Max 5MB)
                      </span>
                      <span className="font-medium text-blue-600 text-sm">Browse File</span>
                    </div>
                  </div>
                </div>
                
                {selectedFile && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Selected: {selectedFile.name}</p>
                    <p className="text-xs text-gray-500">Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                )}
              </div>

              {/* Optional Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Title (Optional)</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter banner title..."
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter banner description..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Redirect URL (Optional)</label>
                  <input
                    type="url"
                    name="redirectUrl"
                    value={formData.redirectUrl}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                  />
                </div>

                {formData.type === 'special_for_you' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => { setShowUploadModal(false); resetForm(); }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={uploadLoading['new']}
                  className={`px-6 py-2 rounded-lg transition-colors ${
                    uploadLoading['new']
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {uploadLoading['new'] ? 'Creating...' : 'Create Banner'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBanner && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold">Edit Banner</h3>
              <button
                onClick={() => { setShowEditModal(false); resetForm(); }}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Current Image */}
              {selectedBanner.image && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Image</label>
                  <img
                    src={selectedBanner.image}
                    alt={selectedBanner.title || 'Banner'}
                    className="w-full max-w-md rounded-lg shadow-sm"
                  />
                </div>
              )}

              {/* Banner Type & Platform */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Banner Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {bannerTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Platform *</label>
                  <select
                    name="platform"
                    value={formData.platform}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {platforms.map(platform => (
                      <option key={platform.value} value={platform.value}>{platform.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* New Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Image (Optional)</label>
                <div className="border border-gray-300 border-dashed rounded-xl hover:border-blue-500 transition-colors">
                  <div
                    {...getRootProps()}
                    className={`p-6 cursor-pointer transition-all ${
                      isDragActive ? "border-blue-500 bg-blue-50" : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <input {...getInputProps()} />
                    <div className="flex flex-col items-center">
                      <div className="mb-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200">
                          <FaArrowCircleUp className="text-xl" />
                        </div>
                      </div>
                      <h4 className="mb-2 font-medium text-gray-800">
                        {isDragActive ? "Drop Files Here" : "Upload New Image"}
                      </h4>
                      <span className="text-sm text-gray-600 mb-2">
                        PNG, JPG, WebP, SVG images (Max 5MB)
                      </span>
                      <span className="font-medium text-blue-600 text-sm">Browse File</span>
                    </div>
                  </div>
                </div>
                
                {selectedFile && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Selected: {selectedFile.name}</p>
                    <p className="text-xs text-gray-500">Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                )}
              </div>

              {/* Form Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Title (Optional)</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter banner title..."
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter banner description..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Redirect URL (Optional)</label>
                  <input
                    type="url"
                    name="redirectUrl"
                    value={formData.redirectUrl}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                  />
                </div>

                {formData.type === 'special_for_you' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => { setShowEditModal(false); resetForm(); }}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={uploadLoading[selectedBanner._id]}
                  className={`px-6 py-2 rounded-lg transition-colors ${
                    uploadLoading[selectedBanner._id]
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {uploadLoading[selectedBanner._id] ? 'Updating...' : 'Update Banner'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedBanner && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold">Banner Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-lg mb-4">Banner Information</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Title:</label>
                      <p className="text-lg font-semibold">{selectedBanner.title || 'Untitled'}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-600">Type:</label>
                      <p className="text-base">{bannerTypes.find(t => t.value === selectedBanner.type)?.label}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-600">Platform:</label>
                      <p className="text-base">{selectedBanner.platform}</p>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-600">Status:</label>
                      <span className={`ml-2 px-3 py-1 text-sm rounded-full ${
                        selectedBanner.status ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedBanner.status ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    {selectedBanner.description && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Description:</label>
                        <p className="text-base">{selectedBanner.description}</p>
                      </div>
                    )}
                    
                    {selectedBanner.redirectUrl && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Redirect URL:</label>
                        <p className="text-base">
                          <a 
                            href={selectedBanner.redirectUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {selectedBanner.redirectUrl}
                          </a>
                        </p>
                      </div>
                    )}
                    
                    {selectedBanner.type === 'special_for_you' && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Display Order:</label>
                        <p className="text-base">{selectedBanner.order || 0}</p>
                      </div>
                    )}
                    
                    <div>
                      <label className="text-sm font-medium text-gray-600">Created:</label>
                      <p className="text-sm text-gray-500">{formatDate(selectedBanner.createdAt)}</p>
                    </div>
                    
                    {selectedBanner.updatedAt && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Last Updated:</label>
                        <p className="text-sm text-gray-500">{formatDate(selectedBanner.updatedAt)}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-lg mb-4">Banner Image</h4>
                  {selectedBanner.image ? (
                    <img
                      src={selectedBanner.image}
                      alt={selectedBanner.title || 'Banner'}
                      className="w-full rounded-lg shadow-sm"
                    />
                  ) : (
                    <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-gray-500">No image available</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-center space-x-3 pt-6 border-t">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(selectedBanner);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Banner
                </button>
                <button
                  onClick={() => handleToggleStatus(selectedBanner)}
                  className={`px-6 py-2 rounded-lg transition-colors ${
                    selectedBanner.status
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {selectedBanner.status ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => setDeleteConfirm(selectedBanner)}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Banner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Confirm Deletion</h3>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "<strong>{deleteConfirm.title || 'this banner'}</strong>"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm._id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleBannerManagement;